/**
 * @file apps/web/src/components/ApprovalStepper.tsx
 * @description Visual stepper for approval workflow chains.
 * Renders sequential, parallel, and conditional step groups.
 * Each step shows approver name, decision badge, timestamps.
 */

import { CheckCircle, XCircle, Clock, UserCheck, SkipForward, Lock } from 'lucide-react';
import { Badge } from './Badge.js';
import { isDelegatedStep } from '../lib/approvalCards.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ApprovalStep {
  id:             string;
  step_order:     number;
  approver_id:    string;
  delegated_to:   string | null;
  approver_name:  string | null;
  approver_email: string | null;
  decision:       'pending' | 'approved' | 'rejected' | 'delegated';
  parallel_group: number | null;
  is_required:    boolean;
  activated_at:   string | null;
  decided_at:     string | null;
  skipped_at:     string | null;
  comment?:       string | null;
}

// A step stays 'pending' through delegation — the engine never persists
// the 'delegated' decision value (see ApprovalEngine.delegate); this is the
// visual signal that the step was reassigned and is still awaiting the
// delegate's actual decision.
//
// Truthiness, not `!== null`: the approvals LIST endpoint omits delegated_to from
// each step, so the field is `undefined` there — which `!== null` read as
// "delegated" on every pending step (see lib/approvalCards.ts).
function isDelegated(step: ApprovalStep): boolean {
  return isDelegatedStep(step);
}

export interface ApprovalStepperProps {
  steps:         ApprovalStep[];
  workflowType:  'sequential' | 'parallel' | 'conditional';
  onDecide?:     (stepId: string, decision: 'approved' | 'rejected') => void;
  currentUserId?: string;
  loading?:      boolean;
}

// ---------------------------------------------------------------------------
// Step node
// ---------------------------------------------------------------------------

function StepIcon({ step, isCurrentUserPending }: { step: ApprovalStep; isCurrentUserPending?: boolean }) {
  if (step.skipped_at) return <SkipForward size={14} className="text-muted" />;
  if (isDelegated(step)) return <UserCheck size={14} className="text-blue" />;

  switch (step.decision) {
    case 'approved':  return <CheckCircle size={14} className="text-success" />;
    case 'rejected':  return <XCircle     size={14} className="text-danger" />;
    default:
      if (isCurrentUserPending) return <Clock size={14} className="text-white" />;
      if (step.activated_at)    return <Clock size={14} className="text-blue" />;
      return <Lock size={14} className="text-muted" />;
  }
}

function stepStateClass(step: ApprovalStep, isCurrentUser: boolean): {
  circle: string;
  row:    string;
  label:  string;
  labelText: string;
} {
  if (step.skipped_at) {
    return {
      circle:    'bg-gray border-2 border-dashed border-muted/40',
      row:       '',
      label:     'text-muted text-[10px] uppercase tracking-wide font-mono',
      labelText: 'Skipped',
    };
  }
  if (isDelegated(step)) {
    return {
      circle:    'bg-sky border-2 border-blue/40',
      row:       '',
      label:     'text-blue text-[10px] uppercase tracking-wide font-mono',
      labelText: 'Delegated',
    };
  }
  switch (step.decision) {
    case 'approved':
      return {
        circle:    'bg-success/12 border-2 border-success/40',
        row:       'opacity-70',
        label:     'text-success text-[10px] uppercase tracking-wide font-mono font-semibold',
        labelText: 'Approved',
      };
    case 'rejected':
      return {
        circle:    'bg-danger/12 border-2 border-danger/40',
        row:       '',
        label:     'text-danger text-[10px] uppercase tracking-wide font-mono font-semibold',
        labelText: 'Rejected',
      };
    default: {
      const isActive = step.activated_at !== null;
      if (isActive && isCurrentUser) {
        return {
          circle:    'bg-blue border-2 border-blue shadow-lg shadow-blue/30 animate-pulse',
          row:       'bg-sky/40 rounded-lg p-2 -mx-2 border border-blue/20',
          label:     'text-blue text-[10px] uppercase tracking-wide font-mono font-bold',
          labelText: 'Pending Your Action',
        };
      }
      if (isActive) {
        return {
          circle:    'bg-sky border-2 border-blue/40',
          row:       '',
          label:     'text-blue text-[10px] uppercase tracking-wide font-mono',
          labelText: 'Pending',
        };
      }
      return {
        circle:    'bg-white border-2 border-[rgba(8,17,31,0.12)]',
        row:       '',
        label:     'text-muted text-[10px] uppercase tracking-wide font-mono',
        labelText: 'Waiting',
      };
    }
  }
}

function ApproverAvatar({ name, email }: { name: string | null; email: string | null }) {
  const display = name ?? email ?? 'U';
  const initials = display
    .split(' ')
    .map((p) => p[0] ?? '')
    .slice(0, 2)
    .join('')
    .toUpperCase();
  return (
    <div
      className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-mono font-bold text-white shrink-0"
      style={{ backgroundColor: 'var(--blue)' }}
      title={display}
    >
      {initials || '?'}
    </div>
  );
}

function formatDate(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function StepCard({
  step,
  connector,
  onDecide,
  currentUserId,
}: {
  step: ApprovalStep;
  connector: 'line' | 'none';
  onDecide?: (stepId: string, decision: 'approved' | 'rejected') => void;
  currentUserId?: string;
}) {
  const isCurrentUser = currentUserId === step.approver_id;
  const isActive      = step.activated_at !== null && step.decision === 'pending' && !step.skipped_at;
  const canDecide     = isActive && isCurrentUser && onDecide;
  const state         = stepStateClass(step, isCurrentUser);

  return (
    <div className={['flex items-start gap-3 mb-1', state.row].join(' ')}>
      {/* Connector + icon column */}
      <div className="flex flex-col items-center shrink-0">
        <div className={['w-9 h-9 rounded-full flex items-center justify-center z-10', state.circle].join(' ')}>
          <StepIcon step={step} isCurrentUserPending={isActive && isCurrentUser} />
        </div>
        {connector === 'line' && (
          <div className="w-px h-4 mt-1" style={{ background: 'rgba(8,17,31,0.1)' }} />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 pb-2">
        {/* State label */}
        <p className={state.label}>{state.labelText}</p>

        {/* Approver name + avatar */}
        <div className="flex items-center gap-1.5 mt-0.5">
          <ApproverAvatar name={step.approver_name} email={step.approver_email} />
          <span className="text-sm font-medium text-ink">
            {step.approver_name ?? step.approver_email ?? 'Unknown User'}
          </span>
          <Badge
            variant={step.skipped_at ? 'skipped' : step.decision}
            size="xs"
          />
          {!step.is_required && (
            <Badge variant="muted" label="Optional" size="xs" />
          )}
        </div>

        {/* Timestamps */}
        {step.activated_at && (
          <p className="text-[11px] font-mono text-muted mt-0.5">
            Activated {formatDate(step.activated_at)}
          </p>
        )}
        {step.decided_at && (
          <p className="text-[11px] font-mono text-muted">
            Decided {formatDate(step.decided_at)}
          </p>
        )}
        {step.comment && (
          <p className="text-xs text-ink/70 italic mt-1 border-l-2 border-blue/30 pl-2">
            "{step.comment}"
          </p>
        )}

        {/* Action buttons — only for active step assigned to current user */}
        {canDecide && (
          <div className="flex gap-2 mt-3">
            <button
              onClick={() => onDecide(step.id, 'approved')}
              className="btn-primary py-1.5 px-4 text-xs font-semibold"
            >
              <CheckCircle size={13} /> Approve
            </button>
            <button
              onClick={() => onDecide(step.id, 'rejected')}
              className="btn-danger py-1.5 px-4 text-xs font-semibold"
            >
              <XCircle size={13} /> Reject
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Parallel group renderer
// ---------------------------------------------------------------------------

function ParallelGroup({
  steps,
  isLast,
  onDecide,
  currentUserId,
}: {
  steps: ApprovalStep[];
  isLast: boolean;
  onDecide?: (stepId: string, decision: 'approved' | 'rejected') => void;
  currentUserId?: string;
}) {
  if (steps.length === 1) {
    return (
      <StepCard
        step={steps[0]!}
        connector={isLast ? 'none' : 'line'}
        onDecide={onDecide}
        currentUserId={currentUserId}
      />
    );
  }

  return (
    <div className="flex items-start gap-3">
      {/* Left line */}
      <div className="flex flex-col items-center pt-0">
        <div className="w-px flex-1 mt-0" style={{ background: 'rgba(8,17,31,0.1)' }} />
      </div>

      {/* Parallel bracket */}
      <div className="flex-1">
        <p className="text-[10px] font-mono font-semibold text-muted uppercase tracking-widest mb-2">
          Parallel group
        </p>
        <div
          className="pl-3 border-l-2 space-y-1"
          style={{ borderColor: 'rgba(8,17,31,0.1)' }}
        >
          {steps.map((step, i) => (
            <StepCard
              key={step.id}
              step={step}
              connector={i < steps.length - 1 ? 'none' : 'none'}
              onDecide={onDecide}
              currentUserId={currentUserId}
            />
          ))}
        </div>
        {!isLast && <div className="w-px h-4 ml-3 mt-0" style={{ background: 'rgba(8,17,31,0.1)' }} />}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------

function Skeleton() {
  return (
    <div className="space-y-4 animate-pulse">
      {[1, 2].map((i) => (
        <div key={i} className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-full bg-gray shrink-0" />
          <div className="flex-1 space-y-1.5 pt-1">
            <div className="h-3.5 bg-gray rounded w-32" />
            <div className="h-3 bg-gray rounded w-24" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function ApprovalStepper({
  steps,
  workflowType,
  onDecide,
  currentUserId,
  loading,
}: ApprovalStepperProps) {
  if (loading) return <Skeleton />;

  if (steps.length === 0) {
    return (
      <div className="text-center py-6 text-sm text-muted">
        No approval steps configured.
      </div>
    );
  }

  // For parallel workflows, group by parallel_group or step_order
  if (workflowType === 'parallel') {
    const groups = new Map<number, ApprovalStep[]>();
    for (const step of steps) {
      const key = step.parallel_group ?? step.step_order;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(step);
    }
    const sortedGroups = [...groups.entries()].sort(([a], [b]) => a - b);

    return (
      <div>
        {sortedGroups.map(([groupKey, groupSteps], i) => (
          <ParallelGroup
            key={groupKey}
            steps={groupSteps}
            isLast={i === sortedGroups.length - 1}
            onDecide={onDecide}
            currentUserId={currentUserId}
          />
        ))}
      </div>
    );
  }

  // Sequential or conditional: render each step in order
  const sorted = [...steps].sort((a, b) => a.step_order - b.step_order);

  return (
    <div>
      {sorted.map((step, i) => (
        <StepCard
          key={step.id}
          step={step}
          connector={i < sorted.length - 1 ? 'line' : 'none'}
          onDecide={onDecide}
          currentUserId={currentUserId}
        />
      ))}
    </div>
  );
}
